import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { services } from '../mock/mockData';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="pt-32 pb-24 text-center">
        <h1 className="font-serif text-cream-50 text-4xl">Service not found</h1>
        <Link to="/services" className="mt-6 inline-block text-sage-400 hover:underline">Back to Services</Link>
      </div>
    );
  }

  const related = service.related
    ? services.filter((s) => service.related.includes(s.slug))
    : services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="pt-24">
      <section className="relative py-28 bg-forest-950">
        <div className="absolute inset-0 opacity-25">
          <img src={service.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-950 via-forest-950/80 to-forest-900" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-cream-100/70 hover:text-sage-400 text-sm mb-6"><ArrowLeft className="h-4 w-4" /> All Services</Link>
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-4">Our expertise</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl leading-tight">{service.name}</h1>
          <p className="mt-6 text-cream-100/80 text-lg md:text-xl max-w-3xl">{service.tagline}</p>
        </div>
      </section>

      <section className="bg-forest-900 py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-6">
            <p className="text-cream-100/85 text-base md:text-lg leading-relaxed">{service.intro}</p>
            <div>
              <h3 className="font-serif text-cream-50 text-2xl mb-4">How we can help</h3>
              <ul className="space-y-3">
                {service.highlights.map((h) => (
                  <li key={h} className="flex gap-3 items-start">
                    <CheckCircle2 className="h-5 w-5 text-sage-400 mt-0.5 flex-shrink-0" />
                    <span className="text-cream-100/85">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            {service.sections?.map((sec) => (
              <div key={sec.heading} data-testid={`service-section-${sec.heading.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <h3 className="font-serif text-cream-50 text-2xl mb-3">{sec.heading}</h3>
                <p className="text-cream-100/85 leading-relaxed">{sec.body}</p>
              </div>
            ))}
          </div>
          <aside className="bg-forest-950 border border-forest-800 p-6 h-fit">
            <h4 className="font-serif text-cream-50 text-xl">Speak to our team</h4>
            <p className="mt-3 text-sm text-cream-100/70">Our {service.name.toLowerCase()} solicitors are here to help. Send us a message and we'll respond promptly.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-6 py-3 text-sm font-medium hover:bg-sage-600 transition-colors">Contact Us <ArrowRight className="h-4 w-4" /></Link>
          </aside>
        </div>
      </section>

      <section className="bg-forest-950 py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <h3 className="section-title font-serif text-cream-50 text-3xl md:text-4xl">Other Services</h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-forest-900 border border-forest-800 hover:border-sage-500/50 transition-colors p-6">
                <h4 className="font-serif text-cream-50 text-xl group-hover:text-sage-400 transition-colors">{s.name}</h4>
                <p className="mt-3 text-cream-100/70 text-sm">{s.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sage-400 text-xs tracking-widest uppercase">Learn more <ArrowRight className="h-3.5 w-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
